
/**
 * A custom logger class based on the 'winston' package.
 *
 * The logger supports four levels of logging: info, debug, warning and error.
 * The format of the message is: [timestamp] [level] : [message] [metadata].
 * All logs with a level of "error" are being sent to Splunk as well.
 *
 * Transports. The transports are the streams where the data is logged. It
 * supports console and Splunk.
 *
 * @type {Logger}
 */
module.exports = class Logger {

  /**
   * Apply a middleware that sets data from the request in a async store.
   *
   * Set data from the request to an async store, in order for the logger
   * to have access to a user id.
   *
   * @param {object} req
   *   Express request object.
   * @param {object} res
   *   Express response object.
   * @param {function} next
   *   Express next middleware function.
   */
  static expressMiddleware(req, res, next) {
    const SplunkTransport = require('./transports/splunk.js');
    SplunkTransport.expressMiddleware(req, res, next);
  }

  /**
   * Returns the metadata object.
   *
   * @return {{appName: (*|string), accountType: (*|string), opco: (*|string),
   *   environment: (*|string), traceId: (*|string), logTime: (*|string)}}
   */
  static getMetadata() {
    return {
      opco: process.env.OPCO_LABEL || "",
      accountType: process.env.ACCOUNT_TYPE || "",
      environment: process.env.ENVIRONMENT || "",
      appName: process.env.APP_NAME || "",
      traceId: process.env.RUNTIME_ID || process.env.CODEBUILD_BUILD_ID || "",
      logTime: new Date().toISOString(),
    }
  }

  /**
   * General method to log in Console and initialize a Splunk request.
   *
   * @param {string} level
   *   The logging level: debug, info, warning, error.
   * @param {string} message
   *   The message to be logged in.
   * @param {string} prefixText
   *   Text that will be logged, before the main message/log/error.
   *   Strongly advised to use it as much as possible, and provide brief, but
   *   meaningful information about the log.
   *   It is also sent to Splunk under the "caller" property.
   * @param {object} context
   *   Additional data to be logged in.
   *   Can be used to pass additional objects, like current user info or
   *   relevant and helpful information.
   * @param {boolean} metadata
   *   A flag whether to log metadata or not.
   * @param {boolean} splunkOnly
   *   A flag whether to log in Splunk only (and not in Cloudwatch).
   */
  static log(level, message, prefixText, context, metadata = true, splunkOnly = true) {
    let loggingMethod = "warning" === level ? "warn" : level;
    if ("function" !== typeof console[loggingMethod]) {
      loggingMethod = "log";
    }

    const data = this.prepareLogData(level, message, prefixText, context, metadata, splunkOnly);

    // Logs during local development.
    if (this.isLocalEnvironment()) {
      if ("object" === typeof message && message.isAxiosError) {
        const prefix = prefixText ? `Encountered axios error - ${prefixText}` : undefined;
        this.logAxiosError(message, prefix, true, context)
      }
      else {
        const localLogData = [];
        // Identifier for easier search.
        localLogData.push(`[${level.toUpperCase()}]`);
        if (prefixText) {
          localLogData.push(prefixText + ":");
        }
        localLogData.push(message);
        if ("object" === typeof context && Object.keys(context).length > 0) {
          localLogData.push(`\n [CONTEXT]: `);
          localLogData.push(context);
        }
        console[loggingMethod](...localLogData);
      }
    }
    // Logs for CloudWatch.
    else {
      if ("object" === typeof message && message.isAxiosError) {
        const prefix = prefixText ? `Encountered axios error - ${prefixText}` : undefined;
        this.logAxiosError(message, prefix, true, context)
      }
      else {
        // Keep CloudWatch logs cleaner - no metadata, level and called are
        // moved before log data object.
        const {metadata, level, caller, message, splunkOnly, ...restOfData} = data;
        // Send log to Cloudwatch only if splunkOnly flag is explicitly set to false.
        if (!splunkOnly) {
          // Do not include messages, that are already strings to restOfData
          // object, in order to avoid potential double JSON stringify.
          const cloudWatchLogStringMessage = "string" === typeof message
            ? message : "";
          if (!cloudWatchLogStringMessage) {
            restOfData.message = message;
          }

          console[loggingMethod](`[${level.toUpperCase()}] ${prefixText}: ${cloudWatchLogStringMessage} ${JSON.stringify(restOfData)}`);
        }
      }
    }

    // Debug splunk data locally.
    if (process.env.LOG_SPLUNK_DATA_LOCALLY) {
      console[loggingMethod]("The following data would be sent to Splunk: ", data);
    }

    // Splunk log.
    if ("undefined" === typeof window) {
      const SplunkTransport = require('./transports/splunk.js');
      (new SplunkTransport()).log(data);
    }
  }

  /**
   * Helper method to extract the caller function from the stack trace.
   *
   * @param {boolean} getCallerFunction
   *   Full stack of where the logger was called, or only caller function.
   *
   * @return {string}
   *   In theory, it must return the caller function.
   */
  static getStackTrace(getCallerFunction = false) {
    try {
      throw new Error();
    } catch (e) {
      if (getCallerFunction) {
        const [, , , , , callerFunction] = e.stack.split("\n");
        return callerFunction.trim();
      }
      return e.stack;
    }
  }

  /**
   * Local logging of axios errors.
   */
  static logAxiosError(errOriginal, prefixText = "Encountered axios error", showStack = false, context = {}) {
    const logValue = {
      isAxiosError: errOriginal.isAxiosError,
      message: errOriginal.message
    };
    if (errOriginal.code) {
      logValue.code = errOriginal.code;
    }
    if (errOriginal.config) {
      const { url, method, headers, baseURL } = errOriginal.config;
      logValue.config = { url, method, headers, baseURL };
    }
    if (errOriginal.request) {
      const { method, path } = errOriginal.request;
      logValue.request = { method, path };
    }
    if (errOriginal.response) {
      const { status, headers, data } = errOriginal.response;
      logValue.response = { status, headers, data };
    }
    if (showStack && errOriginal.stack) {
      logValue.stack = errOriginal.stack;
    }
    if ("object" === typeof context && Object.keys(context).length > 0) {
      logValue.context = context;
    }
    // Do not add spacing when logging for CloudWatch, only locally, as logs in
    // CloudWatch get split for each indentation.
    let stringifyIndentation = null;
    if (this.isLocalEnvironment()) {
      stringifyIndentation = 2;
    }

    console.error("[ERROR] " + prefixText + ": " + JSON.stringify(logValue, null, stringifyIndentation));
  }

  /**
   * Checks if current environment is a local development one.
   *
   * Used in order to format logs differently between developer's working
   * machines and the ones for CloudWatch.
   * @returns {boolean}
   */
  static isLocalEnvironment() {
    return (!process.env.ENVIRONMENT || process.env.ENVIRONMENT === "local");
  }

  /**
   * Prepare data and format message.
   */
  static prepareLogData(level, message, prefixText, context, metadata = true, splunkOnly = true) {
    let stack = typeof message !== "undefined" && typeof message.stack !== "undefined"
      ? message.stack
      : this.getStackTrace(level !== "error");
    if ("object" === typeof message) {
      message = message.response && message.response.data
        ? message.response.data : message;
      if (message.data && message.status) {
        if ("object" === typeof message.data && "object" === typeof message.status) {
          message = {...message.data, ...message.status};
        } else {
          message = {
            "message": message.data,
            "status": message.status
          }
        }
      }
      message = message.message
        ? message.message : message;
    }

    return {
      level,
      caller: prefixText || "",
      message,
      stack: stack,
      context: context && "object" === typeof context ? context : {},
      metadata: metadata ? this.getMetadata() : {},
      splunkOnly
    }
  }

  /**
   * Logs the incoming request's data.
   *
   * @param {Request} request
   *   The Express request object.
   * @param {string} prefixText
   *   Text that will be logged, before the main message/log/error.
   *   Strongly advised to use it as much as possible, and provide brief, but
   *   meaningful information about the log.
   *   It is also sent to Splunk under the "caller" property.
   * @param {object} context
   *   Additional data to be logged.
   * @param metadata
   *   A boolean flag whether the metadata to be logged or not.
   * @param {boolean} splunkOnly
   *   A flag whether to add the log only in Splunk.
   */
  static debug(request, prefixText = "", context = {}, metadata = true, splunkOnly = true) {
    const message = JSON.stringify({
      request: {
        url: request.originalUrl,
        method: request.method,
        headers: request.headers,
        params: request.params,
        query: request.query
      }
    });

    this.log("debug", message, prefixText, context, metadata, splunkOnly);
  }

  /**
   * Logs an info message.
   *
   * @param {string} message
   *   The message that will be logged.
   * @param {string} prefixText
   *   Text that will be logged, before the main message/log/error.
   *   Strongly advised to use it as much as possible, and provide brief, but
   *   meaningful information about the log.
   *   It is also sent to Splunk under the "caller" property.
   * @param {object} context
   *   Additional data to be logged.
   * @param metadata
   *   A boolean flag whether the metadata to be logged or not.
   * @param {boolean} splunkOnly
   *   A flag whether to add the log only in Splunk.
   */
  static info(message, prefixText = "", context = {}, metadata = true, splunkOnly = true) {
    this.log("info", message, prefixText, context, metadata, splunkOnly);
  }

  /**
   * Logs a warning message.
   *
   * @param {string} message
   *   The message that will be logged.
   * @param {string} prefixText
   *   Text that will be logged, before the main message/log/error.
   *   Strongly advised to use it as much as possible, and provide brief, but
   *   meaningful information about the log.
   *   It is also sent to Splunk under the "caller" property.
   * @param {object} context
   *   Additional data to be logged.
   * @param metadata
   *   A boolean flag whether the metadata to be logged or not.
   * @param {boolean} splunkOnly
   *   A flag whether to add the log only in Splunk.
   */
  static warning(message, prefixText = "", context = {}, metadata = true, splunkOnly = true) {
    this.log("warning", message, prefixText, context, metadata, splunkOnly);
  }

  /**
   * Logs an error message and sends it to Splunk.
   *
   * @param {object} error
   *   The message that will be logged.
   * @param {string} prefixText
   *   Text that will be logged, before the main message/log/error.
   *   Strongly advised to use it as much as possible, and provide brief, but
   *   meaningful information about the log.
   *   It is also sent to Splunk under the "caller" property.
   * @param {object} context
   *   Additional data to be logged.
   * @param metadata
   *   A boolean flag whether the metadata to be logged or not.
   * @param {boolean} splunkOnly
   *   A flag whether to add the log only in Splunk.
   */
  static error(error, prefixText = "", context = {}, metadata = true, splunkOnly = false) {
    this.log("error", error, prefixText, context, metadata, splunkOnly);
  }

}
